import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import SearchRequests from "../classes/SearchRequests";
import SearchableView from "./SearchableView";
import Popup from "./Popup";
import PageNavigator from "../classes/PageNavigator";
import Timer from "../utils/Timer";
import ToolTipInfoMessage from "./ToolTipInfoMessage";
import RequestStatus from "../classes/RequestStatus";
import PaginationView from "./PaginationView";
import Query from "../classes/Query";
import NOKUser from "../models/NOKUser";
import MessageDispatch from "../rocket/MessageDispatch";
import StaffAddOrEditRequestView from "./StaffAddOrEditRequestView";
import SearchRequestsRequest from "../models/SearchRequestsRequest";
import ListWrapper from "../utils/ListWrapper";
import Notification from "../models/Notification";
import Administrator from "../models/Administrator";
import Duration from "../core/Duration";
import TextView from "./TextView";
import SearchRequest from "../models/SearchRequest";
import LabelDropdown from "./LabelDropdown";
import CollectionUtils from "../utils/CollectionUtils";
import DateUtils from "../classes/DateUtils";
import { UsageConstants } from "../rocket/D3ETemplate";
import { StyleThemeData } from "./ThemeWrapper";
import { BuildContext } from "../classes/BuildContext";

type _LabelDropdown2OnChanged = (text: RequestStatus) => void;

export interface SystemAdminRequestListProps extends BaseUIProps {
  key?: string;
  admin: Administrator;
  user: NOKUser;
  notification?: Notification;
}

class _SystemAdminRequestListState extends ObservableComponent<SystemAdminRequestListProps> {
  static defaultProps = { admin: null, user: null, notification: null };
  obj: SearchRequest = null;
  query: SearchRequests = null;
  request: SearchRequestsRequest = null;
  requestList: Array<SearchRequest> = ListWrapper.widget(this, "requestList");
  status: RequestStatus = RequestStatus.Processing;
  requestValue: SearchRequest = null;
  services: Array<SearchRequest> = ListWrapper.widget(this, "services");
  isInvisibileNotification: boolean = true;
  staffAddRequestPopup: Popup;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: SystemAdminRequestListProps) {
    super(props);

    this.initState();
  }
  public get admin(): Administrator {
    return this.props.admin;
  }
  public get user(): NOKUser {
    return this.props.user;
  }
  public get notification(): Notification {
    return this.props.notification;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.on(
      [
        "request",
        "request.applyStatus",
        "request.ascending",
        "request.createdBy",
        "request.fromDate",
        "request.hospital",
        "request.name",
        "request.offset",
        "request.orderBy",
        "request.pageSize",
        "request.request",
        "request.status",
        "request.toDate",
      ],
      this.computeQuery
    );

    this.computeQuery();

    this.on(["query", "query.items"], this.computeRequestList);

    this.computeRequestList();

    this.updateSyncProperty("admin", this.props.admin);

    this.updateSyncProperty("user", this.props.user);

    this.computeServices();

    this.updateSyncProperty("notification", this.props.notification);

    this.on(
      [
        "isInvisibileNotification",
        "notification",
        "notification.title",
        "query",
        "query.count",
        "query.items",
        "request",
        "request.offset",
        "request.pageSize",
        "requestList",
        "requestList.createdBy",
        "requestList.createdBy.name",
        "requestList.failedMessage",
        "requestList.hospital",
        "requestList.hospital.name",
        "requestList.requestDate",
        "requestList.searchId",
        "requestList.status",
        "requestValue",
        "services",
        "status",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: SystemAdminRequestListProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.admin !== this.props.admin) {
      this.updateObservable("admin", prevProps.admin, this.props.admin);

      this.fire("admin", this);
    }

    if (prevProps.user !== this.props.user) {
      this.updateObservable("user", prevProps.user, this.props.user);

      this.fire("user", this);
    }

    if (prevProps.notification !== this.props.notification) {
      this.updateObservable(
        "notification",
        prevProps.notification,
        this.props.notification
      );

      this.fire("notification", this);
    }
  }
  public setObj(val: SearchRequest): void {
    let isValChanged: boolean = this.obj !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("obj", this.obj, val);

    this.obj = val;

    this.fire("obj", this);
  }
  public setQuery(val: SearchRequests): void {
    let isValChanged: boolean = this.query !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("query", this.query, val);

    MessageDispatch.get().dispose(this.query);

    this.query = val;

    this.fire("query", this);
  }
  public computeQuery = async (): Promise<void> => {
    try {
      this.setQuery(
        await Query.get().getSearchRequests(
          UsageConstants.QUERY_GETSEARCHREQUESTS_SYSTEMADMINREQUESTLIST_PROPERTIES_QUERY_COMPUTATION,
          this.request,
          { "synchronize": true }
        )
      );
    } catch (exception) {
      console.log(" exception in computeQuery : " + exception.toString());

      this.setQuery(null);
    }
  };
  public setRequest(val: SearchRequestsRequest): void {
    let isValChanged: boolean = this.request !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("request", this.request, val);

    this.request = val;

    this.fire("request", this);
  }
  public setRequestList(val: Array<SearchRequest>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.requestList,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("requestList", this.requestList, val);

    this.requestList.clear();

    this.requestList.addAll(val);

    this.fire("requestList", this);
  }
  public addToRequestList(val: SearchRequest, index: number = -1): void {
    if (index === -1) {
      if (!this.requestList.contains(val)) this.requestList.add(val);
    } else {
      this.requestList.remove(this.requestList.elementAt(index));

      this.requestList.add(val);
    }

    this.fire("requestList", this, val, true);

    this.updateObservable("requestList", null, val);
  }
  public removeFromRequestList(val: SearchRequest): void {
    this.requestList.remove(val);

    this.fire("requestList", this, val, false);

    this.removeObservable("requestList", val);
  }
  public computeRequestList = (): void => {
    try {
      this.setRequestList(Array.from(this.query?.items ?? []));
    } catch (exception) {
      console.log(" exception in computeRequestList : " + exception.toString());

      this.setRequestList([]);
    }
  };
  public setStatus(val: RequestStatus): void {
    let isValChanged: boolean = this.status !== val;

    if (!isValChanged) {
      return;
    }

    this.status = val;

    this.fire("status", this);
  }
  public setRequestValue(val: SearchRequest): void {
    let isValChanged: boolean = this.requestValue !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("requestValue", this.requestValue, val);

    this.requestValue = val;

    this.fire("requestValue", this);
  }
  public setServices(val: Array<SearchRequest>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(this.services, val);

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("services", this.services, val);

    this.services.clear();

    this.services.addAll(val);

    this.fire("services", this);
  }
  public addToServices(val: SearchRequest, index: number = -1): void {
    if (index === -1) {
      if (!this.services.contains(val)) this.services.add(val);
    } else {
      this.services.remove(this.services.elementAt(index));

      this.services.add(val);
    }

    this.fire("services", this, val, true);

    this.updateObservable("services", null, val);
  }
  public removeFromServices(val: SearchRequest): void {
    this.services.remove(val);

    this.fire("services", this, val, false);

    this.removeObservable("services", val);
  }
  public computeServices = async (): Promise<void> => {
    try {
      this.setServices(
        Array.from(
          (
            await Query.get().getSearchRequests(
              UsageConstants.QUERY_GETSEARCHREQUESTS_SYSTEMADMINREQUESTLIST_PROPERTIES_SERVICES_COMPUTATION,
              new SearchRequestsRequest({
                pageSize: 1000,
                offset: 0,
                orderBy: "",
                ascending: true,
              })
            )
          )?.items ?? []
        )
      );
    } catch (exception) {
      console.log(" exception in computeServices : " + exception.toString());

      this.setServices([]);
    }
  };
  public setIsInvisibileNotification(val: boolean): void {
    let isValChanged: boolean = this.isInvisibileNotification !== val;

    if (!isValChanged) {
      return;
    }

    this.isInvisibileNotification = val;

    this.fire("isInvisibileNotification", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      mainAxisAlignment: ui.MainAxisAlignment.start,
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      children: [
        ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.start,
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          children: [
            ui.Container({
              margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 10.0, 0.0, new Map()),
              width: 250,
              child: SearchableView<SearchRequest>({
                placeHolder: "Search",
                name: "Request ID",
                value: this.requestValue,
                items: this.services,
                onChanged: (item) => {
                  this.onChangedLabelDropdown1Handler(item);
                },
              }),
              key: "0",
              className: "x200 hc",
            }),
            ui.Container({
              margin: ui.EdgeInsets.fromLTRB(0.0, 5.0, 0.0, 0.0, new Map()),
              child: LabelDropdown<RequestStatus>({
                placeHolder: "---Select---",
                name: "Status",
                value: this.status,
                items: [null, ...RequestStatus.values],
                width: 250,
                onChanged: (text) => {
                  this.labelDropdown2onChanged(text);

                  this.onChangedLabelDropdown1Handler2(text);
                },
                builder: (context, item) => {
                  return TextView({ data: item === null ? "All" : item.name });
                },
              }),
              key: "1",
              className: "x612",
            }),
          ],
          className: "x142 hc h",
          key: "0",
        }),
        ui.Column({
          children: [
            ui.Column({
              children: [
                ui.Table({
                  defaultColumnWidth: new ui.FlexColumnWidth(1),
                  columnWidths: {
                    0: new ui.FlexColumnWidth(1),
                    1: new ui.FlexColumnWidth(1),
                    2: new ui.FlexColumnWidth(1),
                    3: new ui.FlexColumnWidth(1),
                    4: new ui.FixedColumnWidth(150),
                    5: new ui.FixedColumnWidth(80),
                  },
                  children: [
                    ui.TableRow({
                      children: [
                        ui.TableCell({
                          verticalAlignment:
                            ui.TableCellVerticalAlignment.middle,
                          child: TextView({
                            data: "Request ID",
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "0",
                        }),
                        ui.TableCell({
                          verticalAlignment:
                            ui.TableCellVerticalAlignment.middle,
                          child: TextView({
                            data: "Hospital",
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "1",
                        }),
                        ui.TableCell({
                          verticalAlignment:
                            ui.TableCellVerticalAlignment.middle,
                          child: TextView({
                            data: "Created User",
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "2",
                        }),
                        ui.TableCell({
                          verticalAlignment:
                            ui.TableCellVerticalAlignment.middle,
                          child: TextView({
                            data: "Date Time",
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "3",
                        }),
                        ui.TableCell({
                          verticalAlignment:
                            ui.TableCellVerticalAlignment.middle,
                          child: TextView({
                            data: "Status",
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "4",
                        }),
                      ],
                      className: "xc56 hc",
                      key: "0",
                    }),
                    this.query !== null && this.query.items.isNotEmpty
                      ? this.requestList.expand((forItem) => [
                          ui.TableRow({
                            states: ui.joinStates(
                              {
                                "data-c0":
                                  this.notification !== null &&
                                  forItem.searchId ===
                                    this.notification.title &&
                                  this.isInvisibileNotification,
                              },
                              {}
                            ),
                            decoration:
                              this.notification !== null &&
                              forItem.searchId === this.notification.title &&
                              this.isInvisibileNotification
                                ? new ui.BoxDecoration({
                                    color: new ui.Color(0xffe9ecf2),
                                  })
                                : new ui.BoxDecoration({}),
                            children: [
                              ui.TableCell({
                                verticalAlignment:
                                  ui.TableCellVerticalAlignment.middle,
                                child: TextView({
                                  data: forItem.searchId,
                                  softWrap:
                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                  style: new ui.TextStyle({
                                    fontFamily:
                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                    color: cStyle.tTextViewTableCellTextColorOn,
                                    fontSize:
                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                  }),
                                  className: "hc",
                                }),
                                className: "hc",
                                key: "0",
                              }),
                              ui.TableCell({
                                verticalAlignment:
                                  ui.TableCellVerticalAlignment.middle,
                                child: TextView({
                                  data: forItem.hospital.name,
                                  softWrap:
                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                  style: new ui.TextStyle({
                                    fontFamily:
                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                    color: cStyle.tTextViewTableCellTextColorOn,
                                    fontSize:
                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                  }),
                                  className: "hc",
                                }),
                                className: "hc",
                                key: "1",
                              }),
                              ui.TableCell({
                                verticalAlignment:
                                  ui.TableCellVerticalAlignment.middle,
                                child: TextView({
                                  data: forItem.createdBy.name,
                                  softWrap:
                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                  style: new ui.TextStyle({
                                    fontFamily:
                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                    color: cStyle.tTextViewTableCellTextColorOn,
                                    fontSize:
                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                  }),
                                  className: "hc",
                                }),
                                className: "hc",
                                key: "2",
                              }),
                              ui.TableCell({
                                verticalAlignment:
                                  ui.TableCellVerticalAlignment.middle,
                                child: TextView({
                                  data: DateUtils.format(forItem.requestDate),
                                  softWrap:
                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                  style: new ui.TextStyle({
                                    fontFamily:
                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                    color: cStyle.tTextViewTableCellTextColorOn,
                                    fontSize:
                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                  }),
                                  className: "hc",
                                }),
                                className: "hc",
                                key: "3",
                              }),
                              ui.TableCell({
                                verticalAlignment:
                                  ui.TableCellVerticalAlignment.middle,
                                child: ui.Row({
                                  children: [
                                    TextView({
                                      data: forItem.status.name,
                                      softWrap:
                                        cStyle.tTextViewTableCellTextSoftWrapOn,
                                      states: ui.joinStates(
                                        {
                                          "data-c0":
                                            forItem.status ===
                                            RequestStatus.Processing,
                                          "data-c1":
                                            forItem.status ===
                                            RequestStatus.Resolved,
                                          "data-c2":
                                            forItem.status ===
                                            RequestStatus.Failed,
                                        },
                                        {}
                                      ),
                                      style: new ui.TextStyle({
                                        fontFamily:
                                          cStyle.tTextViewTableCellTextFontFamilyOn,
                                        color:
                                          forItem.status ===
                                          RequestStatus.Processing
                                            ? cStyle.c25
                                            : forItem.status ===
                                              RequestStatus.Resolved
                                            ? cStyle.c24
                                            : forItem.status ===
                                              RequestStatus.Failed
                                            ? cStyle.c25
                                            : cStyle.tTextViewTableCellTextColorOn,
                                        fontSize:
                                          cStyle.tTextViewTableCellTextFontSizeOn,
                                      }),
                                      className: "x8bc",
                                      key: "0",
                                    }),
                                    forItem.status === RequestStatus.Failed
                                      ? ToolTipInfoMessage({
                                          message: forItem.failedMessage,
                                        })
                                      : ui.Container({}),
                                  ],
                                  className: "xc72 hc h",
                                }),
                                className: "x431 hc h",
                                key: "4",
                              }),
                            ],
                            onTap: (e) => {
                              e.stopPropagation();

                              this.onEditHandler(forItem);
                            },
                            className: "x900 hc h",
                            key: forItem?.ident,
                          }),
                        ])
                      : [],
                  ],
                  className: "x5eb hc h",
                  key: "0",
                }),
                this.query !== null && this.query.items.isEmpty
                  ? ui.Column({
                      mainAxisAlignment: ui.MainAxisAlignment.center,
                      children: [
                        ui.AssetImage({
                          path: "images/emptyList.png",
                          width: 300,
                          height: 300,
                          className: "x2f3 hc vc",
                          key: "0",
                        }),
                        TextView({
                          data: "No Records",
                          style: new ui.TextStyle({
                            fontWeight: ui.FontWeight.bold,
                            fontSize: 16,
                          }),
                          className: "xbff hc",
                          key: "1",
                        }),
                      ],
                      className: "x825d hc",
                    })
                  : [],
                ui.Row({
                  mainAxisAlignment: ui.MainAxisAlignment.end,
                  children: [
                    PaginationView({
                      pageSize: this.request.pageSize,
                      total: this.query !== null ? this.query.count : 0,
                      offset: this.request.offset,
                      onChange: (pageSize, offset) => {
                        this.onPaginationViewChanged(pageSize, offset);
                      },
                      states: ui.joinStates(
                        {
                          "data-visibility":
                            this.query !== null && this.query.items.isNotEmpty,
                        },
                        {}
                      ),
                      key: "0",
                    }),
                  ],
                  className: "x4f2 hc h",
                  key: "2",
                }),
              ],
              className: "xbb1a hc h",
              key: "0",
            }),
          ],
          className: "x06a hc h",
          key: "1",
        }),
      ],
      className: ui.join(
        this.props.className,
        "SystemAdminRequestList x123 hc vc"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = (): void => {
    new Timer(new Duration({ seconds: 5 }), () => {
      this.setIsInvisibileNotification(false);
    });

    this.setRequestValue(null);

    this.setStatus(null);

    this.setRequest(
      new SearchRequestsRequest({
        pageSize: 50,
        offset: 0,
        orderBy: "",
        ascending: false,
        applyStatus: false,
      })
    );
  };
  public onPaginationViewChanged = (pageSize: number, offset: number): void => {
    this.request.setPageSize(pageSize);

    this.request.setOffset(offset);

    this.setRequest(this.request.deepClone());
  };
  public onChangedLabelDropdown1Handler2 = (value: RequestStatus): void => {
    this.setStatus(value);

    if (value !== null) {
      this.setRequest(
        new SearchRequestsRequest({
          pageSize: 50,
          status: value,
          offset: 0,
          orderBy: "sortOrder",
          ascending: true,
          applyStatus: true,
        })
      );
    } else {
      this.setRequest(
        new SearchRequestsRequest({
          pageSize: 50,
          status: value,
          offset: 0,
          orderBy: "sortOrder",
          ascending: true,
          applyStatus: false,
        })
      );
    }

    this.setRequest(this.request.deepClone());
  };
  public onChangedLabelDropdown1Handler = (item: SearchRequest): void => {
    this.setRequestValue(item);

    this.setRequest(
      new SearchRequestsRequest({
        pageSize: 50,
        request: item,
        status: this.status,
        offset: 0,
        orderBy: "sortOrder",
        ascending: true,
        applyStatus: false,
      })
    );

    this.setRequest(this.request.deepClone());
  };
  public onEditHandler = (forItem: SearchRequest): void => {
    this.navigator.pushSearchRequestResponseView({
      obj: forItem,
      admin: this.admin,
      target: "main",
      replace: false,
    });
  };
  public dispose(): void {
    MessageDispatch.get().dispose(this.query);

    this.staffAddRequestPopup?.dispose();

    super.dispose();
  }
  public showStaffAddRequest(
    d3eParams?: Partial<{
      autoClose: boolean;
      model: boolean;
      float: boolean;
      takeFocus: boolean;
    }>
  ): void {
    let autoClose = d3eParams?.autoClose;

    let model = d3eParams?.model;

    let float = d3eParams?.float;

    let takeFocus = d3eParams?.takeFocus;

    this.staffAddRequestPopup?.dispose();

    /*
TODO ThemeWrapper.of(this.context);
*/

    let cStyle = StyleThemeData.current;

    this.staffAddRequestPopup = new Popup({
      autoClose: autoClose,
      model: model,
      float: float,
      takeFocus: takeFocus,
      position: ui.PopUpPosition.Center,
      child: ui.Container({
        width: 900,
        child: StaffAddOrEditRequestView({
          obj: this.obj,
          admin: this.admin,
          user: this.user,
        }),
        className: "xec3 hc vc",
      }),
    });

    this.staffAddRequestPopup.showPopup(this.context);
  }
  public hideStaffAddRequest(): void {
    this.staffAddRequestPopup?.dispose();
  }
  public labelDropdown2onChanged = (val: RequestStatus): void => {
    this.setStatus(val);
  };
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
}
export default function SystemAdminRequestList(
  props: SystemAdminRequestListProps
) {
  return React.createElement(_SystemAdminRequestListState, {
    ..._SystemAdminRequestListState.defaultProps,
    ...props,
  });
}
